import { useCallback, useMemo } from "react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { useQueryClient } from "react-query"
import { getModuleFromUserContext, useUser, useWorkTask } from "@tm/context-distribution"
import { Articles } from "@tm/data"
import { Article, ArticleListType, CrossSellingModalType, ECrossSellingType, UserModuleType } from "@tm/models"
import { isSameArticle } from "@tm/utils"
import { getCachedWorkTaskBasketData } from "@bundles/basket/data/hooks/workTaskBasket"
import { CrossSellingCombinedStates, crossSellingListId, getCrossSellingDefaultState } from "../crossSellingStates"
import { addArticlesToChain, createRecommendedArticlesRequest, getChainKey, isCrossSellingEnabledForList } from "../helpers"
import { CrossSellingTypeValues } from "../models"
import { QuantitySuggestionEnabledSelector } from "../../../states"

// this hook should not contain contexts from parts list v2!
export function useCrossSellingActions(
    listType: ArticleListType | undefined,
    isAlternativeSearch?: boolean,
    crossSellingModalType?: CrossSellingModalType | undefined
) {
    const queryClient = useQueryClient()
    const { userContext } = useUser()

    const enableCrossSelling = useMemo<boolean>(() => !!getModuleFromUserContext(userContext, UserModuleType.CrossSelling), [userContext.modules])

    const quantitySuggestionEnabled = useRecoilValue(QuantitySuggestionEnabledSelector)

    const workTask = useWorkTask()!
    const vehicle = workTask.workTask?.vehicle
    const workTaskId = workTask.workTaskId!

    const setCrossSellingData = useSetRecoilState(CrossSellingCombinedStates(workTaskId))

    const loadArticles = useCallback(
        async (referenceArticles: Article[]) => {
            const workTaskBasket = await getCachedWorkTaskBasketData(queryClient, workTaskId, true)

            const request = createRecommendedArticlesRequest(workTaskBasket?.parts, referenceArticles, vehicle)

            if (!request) {
                return undefined
            }

            const queryKey = [crossSellingListId, JSON.stringify(request)]
            try {
                const response = await queryClient.fetchQuery({
                    queryKey,
                    queryFn: () => Articles.getRecommendedArticles(request),
                    staleTime: 5 * 60 * 1000,
                })

                const updatedArticles =
                    quantitySuggestionEnabled && response?.articles?.length
                        ? response.articles.map((a) => ({
                              ...a,
                              ...(a.suggestedQuantity && {
                                  quantity: a.suggestedQuantity,
                                  initialQuantity: a.suggestedQuantity,
                              }),
                          }))
                        : response?.articles

                return {
                    alsoBoughtArticles: updatedArticles ?? [],
                    alsoBoughtProductGroups: response?.customerAlsoBoughtProductGroups ?? [],
                    recommendedProductGroups: response?.recommendedProductGroups ?? [],
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Fetch failed:", error)

                return {
                    alsoBoughtArticles: [],
                    alsoBoughtProductGroups: [],
                    recommendedProductGroups: [],
                }
            }
        },
        [queryClient, workTaskId, vehicle, quantitySuggestionEnabled]
    )

    const loadRecommendedArticles = useCallback(
        async (articles: Article[], articleOrder?: number) => {
            if (!enableCrossSelling || !isCrossSellingEnabledForList(listType, isAlternativeSearch, crossSellingModalType)) {
                return
            }

            let referenceArticle: Article | undefined

            setCrossSellingData((prev) => {
                referenceArticle = prev.referenceArticle
                return {
                    ...prev,
                    isLoading: true,
                    referenceArticle:
                        referenceArticle && articles.some((article) => referenceArticle && isSameArticle(article, referenceArticle))
                            ? referenceArticle
                            : undefined,
                }
            })

            const response = await loadArticles(
                articles.map((article, idx) => ({
                    ...article,
                    isSelected: idx === articles.length - 1,
                }))
            )

            const resetChains = !articles.some((article) => referenceArticle && isSameArticle(article, referenceArticle))

            setCrossSellingData((prev) => ({
                ...prev,
                isLoading: false,
                articleOrder,
                referenceArticle: articles.last(),
                data: {
                    alsoBoughtArticles: response?.alsoBoughtArticles || [],
                    alsoBoughtProductGroups: response?.alsoBoughtProductGroups || [],
                    recommendedProductGroups: response?.recommendedProductGroups || [],
                },
                chains: {
                    alsoBoughtArticles: resetChains || !response?.alsoBoughtArticles?.length ? [] : prev.chains.alsoBoughtArticles,
                    alsoBoughtProductGroups: resetChains || !response?.alsoBoughtProductGroups?.length ? [] : prev.chains.alsoBoughtProductGroups,
                    recommendedProductGroups: resetChains || !response?.recommendedProductGroups?.length ? [] : prev.chains.recommendedProductGroups,
                },
            }))
        },
        [crossSellingModalType, enableCrossSelling, isAlternativeSearch, listType, loadArticles, setCrossSellingData]
    )

    const loadNextRecommendation = useCallback(
        async (articles: Article[]) => {
            if (!enableCrossSelling) {
                return
            }

            let referenceArticle: Article | undefined

            setCrossSellingData((prev) => {
                referenceArticle = prev.referenceArticle
                return {
                    ...prev,
                    isLoading: true,
                }
            })

            const articlesToLoad =
                referenceArticle && !articles.some((a) => a.isSelected) ? [{ ...referenceArticle, isSelected: true }, ...articles] : [...articles]

            const response = await loadArticles(articlesToLoad)

            const currentChainKey = getChainKey(crossSellingModalType)

            setCrossSellingData((prev) => {
                const updatedChains: Record<ECrossSellingType, Article[][]> = {
                    [ECrossSellingType.alsoBoughtArticles]: [],
                    [ECrossSellingType.alsoBoughtProductGroups]: [],
                    [ECrossSellingType.recommendedProductGroups]: [],
                }

                CrossSellingTypeValues.forEach((key) => {
                    if (key === currentChainKey) {
                        const chain = !response?.[currentChainKey]?.length ? [] : prev.chains[currentChainKey]

                        updatedChains[key] = !response?.[currentChainKey]?.length ? [] : addArticlesToChain(chain, articles)
                    } else {
                        updatedChains[key] = !response?.[key]?.length ? [] : prev.chains[key]
                    }
                })

                return {
                    ...prev,
                    isLoading: false,
                    data: {
                        alsoBoughtArticles: response?.alsoBoughtArticles || [],
                        recommendedProductGroups: response?.recommendedProductGroups || [],
                        alsoBoughtProductGroups: response?.alsoBoughtProductGroups || [],
                    },
                    chains: updatedChains,
                }
            })
        },
        [enableCrossSelling, setCrossSellingData, loadArticles, crossSellingModalType]
    )

    const reset = useCallback(() => {
        if (!enableCrossSelling) {
            return
        }

        setCrossSellingData(getCrossSellingDefaultState())
    }, [enableCrossSelling, setCrossSellingData])

    return useMemo(
        () => ({
            loadRecommendation: (refArticle: Article[], articleOrder?: number) => {
                // this is when you open the search in modal from the product group buttons
                if (
                    crossSellingModalType === ECrossSellingType.recommendedProductGroups ||
                    crossSellingModalType === ECrossSellingType.alsoBoughtProductGroups
                ) {
                    loadNextRecommendation(refArticle)
                } else {
                    loadRecommendedArticles(refArticle, articleOrder)
                }
            },
            loadNextRecommendation,
            reset,
        }),
        [loadNextRecommendation, reset, crossSellingModalType, loadRecommendedArticles]
    )
}
